/*
 * edit.js — every change a person makes to a track, as a pure function
 * Doc → Doc. Nothing is mutated: the page keeps the previous Doc on its undo
 * stack, and typed arrays that did not change are shared, not copied.
 *
 * Indices are FLAT indices over a track's segments (the ones GpsGeo.series
 * and the charts use); every op maps them back to (segment, offset) itself.
 */
(function () {
    'use strict';
    var root = typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : this);
    var G = function () { return root.GpsGeo; };

    function cloneDoc(doc, tracks, extra) {
        var d = {};
        for (var k in doc) d[k] = doc[k];
        d.tracks = tracks || doc.tracks.slice();
        if (extra) for (var e in extra) d[e] = extra[e];
        return d;
    }
    function cloneTrack(t, segs, extra) {
        var o = {};
        for (var k in t) o[k] = t[k];
        o.segs = segs || t.segs.slice();
        if (extra) for (var e in extra) o[e] = extra[e];
        return o;
    }
    function subSeg(s, a, b) { // inclusive range inside one segment
        if (a === 0 && b === s.n - 1) return s;
        var o = { n: b - a + 1, lat: s.lat.slice(a, b + 1), lon: s.lon.slice(a, b + 1),
                  ele: s.ele ? s.ele.slice(a, b + 1) : null, time: s.time ? s.time.slice(a, b + 1) : null, ch: {} };
        for (var c in s.ch) o.ch[c] = s.ch[c].slice(a, b + 1);
        return o;
    }
    function locate(t, i) { // flat index → [seg, offset]
        var o = 0;
        for (var s = 0; s < t.segs.length; s++) {
            if (i < o + t.segs[s].n) return [s, i - o];
            o += t.segs[s].n;
        }
        return [t.segs.length - 1, t.segs[t.segs.length - 1].n - 1];
    }
    function rangeSegs(t, from, to) {
        var out = [], o = 0;
        for (var s = 0; s < t.segs.length; s++) {
            var S = t.segs[s], a = Math.max(from, o), b = Math.min(to, o + S.n - 1);
            if (a <= b) out.push(subSeg(S, a - o, b - o));
            o += S.n;
        }
        return out;
    }
    function setTrack(doc, ti, t) {
        var tr = doc.tracks.slice();
        tr[ti] = t;
        return cloneDoc(doc, tr);
    }
    function total(t) { return t.segs.reduce(function (s, x) { return s + x.n; }, 0); }
    function lapsIn(t, segs) {
        // keep only laps that still start inside the remaining time span
        if (!t.laps || !t.laps.length) return [];
        var t0 = Infinity, t1 = -Infinity;
        segs.forEach(function (s) { if (s.time) for (var i = 0; i < s.n; i++) { var v = s.time[i]; if (!isNaN(v)) { if (v < t0) t0 = v; if (v > t1) t1 = v; } } });
        return t.laps.filter(function (l) { return l.start != null && l.start >= t0 - 1000 && l.start <= t1; });
    }

    // Edits that change the geometry make the device's own totals stale.
    function edited(t, segs) { return cloneTrack(t, segs, { device: null, laps: lapsIn(t, segs), edited: true }); }

    var E = {
        crop: function (doc, ti, from, to) {
            var t = doc.tracks[ti];
            return setTrack(doc, ti, edited(t, rangeSegs(t, from, to)));
        },
        cut: function (doc, ti, from, to) {
            var t = doc.tracks[ti], n = total(t);
            var segs = [];
            if (from > 0) segs = segs.concat(rangeSegs(t, 0, from - 1));
            if (to < n - 1) segs = segs.concat(rangeSegs(t, to + 1, n - 1));
            return setTrack(doc, ti, edited(t, segs));
        },
        split: function (doc, ti, at) {
            var t = doc.tracks[ti], n = total(t);
            if (at <= 0 || at >= n - 1) return doc;
            var a = edited(t, rangeSegs(t, 0, at)), b = edited(t, rangeSegs(t, at, n - 1));
            a.name = (t.name || '') + (t.name ? ' ' : '') + '(1)';
            b.name = (t.name || '') + (t.name ? ' ' : '') + '(2)';
            b.color = null;
            var tr = doc.tracks.slice();
            tr.splice(ti, 1, a, b);
            return cloneDoc(doc, tr);
        },
        /* Merge: tracks are ordered by start time when every one has a time,
         * otherwise kept in the given order. `connect` joins them into ONE
         * segment (Garmin Connect reads only the first <trkseg> of a GPX), the
         * default keeps them as separate segments so no distance is invented
         * across the gap. */
        merge: function (doc, idxs, connect) {
            var list = idxs.map(function (i) { return doc.tracks[i]; });
            var timed = list.every(function (t) { return t.segs.some(function (s) { return s.time; }); });
            var start = function (t) {
                for (var s = 0; s < t.segs.length; s++) { var tt = t.segs[s].time; if (tt) for (var i = 0; i < tt.length; i++) if (!isNaN(tt[i])) return tt[i]; }
                return Infinity;
            };
            if (timed) list = list.slice().sort(function (a, b) { return start(a) - start(b); });
            var segs = [];
            list.forEach(function (t) { segs = segs.concat(t.segs); });
            if (connect && segs.length > 1) segs = [joinSegs(segs)];
            var first = list[0];
            var m = cloneTrack(first, segs, { device: null, laps: [], edited: true, name: first.name || null });
            var tr = doc.tracks.filter(function (_, i) { return idxs.indexOf(i) < 0; });
            tr.splice(Math.min.apply(null, idxs), 0, m);
            return cloneDoc(doc, tr);
        },
        joinSegments: function (doc, ti) {
            var t = doc.tracks[ti];
            if (t.segs.length < 2) return doc;
            return setTrack(doc, ti, cloneTrack(t, [joinSegs(t.segs)], { edited: true }));
        },
        /* Reverse the direction. Times are MIRRORED (t' = t0 + t1 − t), so the
         * reversed track still runs forward in time and keeps its pace — the
         * only honest thing a file can say about a walk it never took. */
        reverse: function (doc, ti) {
            var t = doc.tracks[ti];
            var segs = t.segs.slice().reverse().map(function (s) {
                var o = { n: s.n, lat: s.lat.slice().reverse(), lon: s.lon.slice().reverse(), ele: s.ele ? s.ele.slice().reverse() : null, time: null, ch: {} };
                for (var c in s.ch) if (c !== 'dist') o.ch[c] = s.ch[c].slice().reverse();
                return o;
            });
            var t0 = Infinity, t1 = -Infinity;
            t.segs.forEach(function (s) { if (s.time) for (var i = 0; i < s.n; i++) { var v = s.time[i]; if (!isNaN(v)) { if (v < t0) t0 = v; if (v > t1) t1 = v; } } });
            if (isFinite(t0)) {
                var orig = t.segs.slice().reverse();
                segs.forEach(function (s, k) {
                    var src = orig[k].time;
                    if (!src) return;
                    s.time = new Float64Array(s.n);
                    for (var i = 0; i < s.n; i++) { var v = src[s.n - 1 - i]; s.time[i] = isNaN(v) ? NaN : t0 + t1 - v; }
                });
            }
            return setTrack(doc, ti, cloneTrack(t, segs, { device: null, laps: [], edited: true }));
        },
        keepMask: function (doc, ti, mask) { // mask over flat indices: 1 = keep
            var t = doc.tracks[ti], o = 0;
            var segs = t.segs.map(function (s) {
                var idx = [];
                for (var i = 0; i < s.n; i++) if (mask[o + i]) idx.push(i);
                o += s.n;
                return pick(s, idx);
            }).filter(function (s) { return s && s.n; });
            return setTrack(doc, ti, cloneTrack(t, segs, { edited: true, laps: lapsIn(t, segs) }));
        },
        removePoints: function (doc, ti, list) {
            var n = total(doc.tracks[ti]), mask = new Uint8Array(n).fill(1);
            list.forEach(function (i) { mask[i] = 0; });
            return E.keepMask(doc, ti, mask);
        },
        simplify: function (doc, ti, target) {
            var sr = G().series(doc.tracks[ti]);
            var r = G().simplifyTo(sr.lat, sr.lon, target, sr.segStart.concat(sr.segStart.map(function (s, k) { return (k + 1 < sr.segStart.length ? sr.segStart[k + 1] : sr.n) - 1; })));
            return { doc: E.keepMask(doc, ti, r.mask), tol: r.tol, kept: r.kept };
        },
        /* Privacy: drop everything within R metres of the start and of the end,
         * plus a random extra (up to R/2) so the cut edge does not sit on a
         * circle whose centre is the doorstep — the attack in Hassan et al.,
         * USENIX Security 2018 (95 % of fixed-radius zones were reversible). */
        privacyTrim: function (doc, ti, radius, rnd) {
            var t = doc.tracks[ti], sr = G().series(t), n = sr.n;
            if (n < 3) return doc;
            rnd = rnd || Math.random;
            var r0 = radius + rnd() * radius / 2, r1 = radius + rnd() * radius / 2;
            var d = G().dist;
            var a = 0, b = n - 1;
            while (a < n - 1 && d(sr.lat[0], sr.lon[0], sr.lat[a], sr.lon[a]) < r0) a++;
            while (b > a && d(sr.lat[n - 1], sr.lon[n - 1], sr.lat[b], sr.lon[b]) < r1) b--;
            if (b - a < 2) return doc;
            var res = E.crop(doc, ti, a, b);
            res.tracks[ti].privacy = { radius: radius, removedStart: a, removedEnd: n - 1 - b };
            return res;
        },
        strip: function (doc, what) { // what: {time, ele, hr, cad, power, temp, meta}
            var tr = doc.tracks.map(function (t) {
                var segs = t.segs.map(function (s) {
                    var o = { n: s.n, lat: s.lat, lon: s.lon, ele: what.ele ? null : s.ele, time: what.time ? null : s.time, ch: {} };
                    for (var c in s.ch) {
                        if (what[c]) continue;
                        if (what.time && (c === 'speed' || c === 'dist')) continue;
                        o.ch[c] = s.ch[c];
                    }
                    return o;
                });
                var nt = cloneTrack(t, segs, { edited: true });
                if (what.time) { nt.laps = []; nt.device = null; }
                if (what.meta) { nt.desc = null; }
                return nt;
            });
            var wp = what.time ? doc.waypoints.map(function (w) { var o = {}; for (var k in w) o[k] = w[k]; o.time = null; return o; }) : doc.waypoints;
            var d = cloneDoc(doc, tr, { waypoints: wp });
            if (what.meta) { d.device = null; d.creator = null; d.time = what.time ? null : d.time; }
            return d;
        },
        shiftTime: function (doc, ms, ti) {
            var tr = doc.tracks.map(function (t, k) {
                if (ti != null && k !== ti) return t;
                var segs = t.segs.map(function (s) {
                    if (!s.time) return s;
                    var o = subSeg(s, 0, s.n - 1);
                    if (o === s) { o = { n: s.n, lat: s.lat, lon: s.lon, ele: s.ele, time: null, ch: s.ch }; }
                    o.time = new Float64Array(s.n);
                    for (var i = 0; i < s.n; i++) o.time[i] = isNaN(s.time[i]) ? NaN : s.time[i] + ms;
                    return o;
                });
                var laps = (t.laps || []).map(function (l) { var o = {}; for (var q in l) o[q] = l[q]; if (o.start != null) o.start += ms; if (o.end != null) o.end += ms; return o; });
                return cloneTrack(t, segs, { laps: laps });
            });
            return cloneDoc(doc, tr);
        },
        /* Give an untimed route a timeline (Strava and Garmin Connect refuse a
         * GPX with no <time>). Constant speed on the flat, slowed on climbs by
         * Tobler's hiking function when the sport is on foot. */
        addTimes: function (doc, ti, startMs, speedMs, onFoot) {
            var t = doc.tracks[ti], sr = G().series(t), gr = onFoot ? G().grade(sr, 60) : null;
            var times = new Float64Array(sr.n), acc = startMs;
            times[0] = acc;
            for (var i = 1; i < sr.n; i++) {
                var dd = sr.geoDist[i] - sr.geoDist[i - 1];
                var v = speedMs;
                if (gr && !isNaN(gr[i])) v = speedMs * Math.exp(-3.5 * Math.abs(gr[i] / 100 + 0.05)) / Math.exp(-3.5 * 0.05);
                acc += dd / Math.max(0.3, v) * 1000;
                times[i] = Math.round(acc);
            }
            var o = 0;
            var segs = t.segs.map(function (s) {
                var ns = { n: s.n, lat: s.lat, lon: s.lon, ele: s.ele, time: times.slice(o, o + s.n), ch: s.ch };
                o += s.n;
                return ns;
            });
            return setTrack(doc, ti, cloneTrack(t, segs, { edited: true, kind: 'track' }));
        },
        /* A timeline computed elsewhere (analysis.estimate): offsets in
         * seconds from `startMs`, one per flat point. */
        setTimes: function (doc, ti, startMs, offsets) {
            var t = doc.tracks[ti], o = 0;
            var segs = t.segs.map(function (s) {
                var tm = new Float64Array(s.n);
                for (var k = 0; k < s.n; k++) tm[k] = Math.round(startMs + offsets[o + k] * 1000);
                o += s.n;
                return { n: s.n, lat: s.lat, lon: s.lon, ele: s.ele, time: tm, ch: s.ch };
            });
            return setTrack(doc, ti, cloneTrack(t, segs, { edited: true, kind: 'track' }));
        },
        setElevation: function (doc, ti, ele) {
            var t = doc.tracks[ti], o = 0;
            var segs = t.segs.map(function (s) {
                var ns = { n: s.n, lat: s.lat, lon: s.lon, ele: ele.slice(o, o + s.n), time: s.time, ch: s.ch };
                o += s.n;
                return ns;
            });
            return setTrack(doc, ti, cloneTrack(t, segs, { edited: true, eleSource: 'dem' }));
        },
        setKind: function (doc, ti, kind) { return setTrack(doc, ti, cloneTrack(doc.tracks[ti], null, { kind: kind })); },
        rename: function (doc, ti, name) { return setTrack(doc, ti, cloneTrack(doc.tracks[ti], null, { name: name || null })); },
        setColor: function (doc, ti, color) { return setTrack(doc, ti, cloneTrack(doc.tracks[ti], null, { color: color })); },
        setSport: function (doc, ti, sport) { return setTrack(doc, ti, cloneTrack(doc.tracks[ti], null, { sport: sport })); },
        removeTrack: function (doc, ti) { return cloneDoc(doc, doc.tracks.filter(function (_, i) { return i !== ti; })); },
        removeWaypoint: function (doc, wi) { return cloneDoc(doc, null, { waypoints: doc.waypoints.filter(function (_, i) { return i !== wi; }) }); },
        addWaypoint: function (doc, w) { return cloneDoc(doc, null, { waypoints: doc.waypoints.concat([w]) }); },
        /* several Docs (several files) into one, remembering which file each track came from */
        combine: function (docs) {
            var out = { format: 'multi', name: null, creator: null, device: null, time: null, tracks: [], waypoints: [], areas: [], warnings: [] };
            docs.forEach(function (d) {
                out.tracks = out.tracks.concat(d.tracks);
                out.waypoints = out.waypoints.concat(d.waypoints);
                out.areas = out.areas.concat(d.areas || []);
                if (!out.name && d.name) out.name = d.name;
            });
            return out;
        },
        _locate: locate, _rangeSegs: rangeSegs, _total: total
    };

    function pick(s, idx) {
        if (!idx.length) return null;
        if (idx.length === s.n) return s;
        var n = idx.length, o = { n: n, lat: new Float64Array(n), lon: new Float64Array(n), ele: s.ele ? new Float32Array(n) : null, time: s.time ? new Float64Array(n) : null, ch: {} };
        for (var c in s.ch) o.ch[c] = new Float32Array(n);
        for (var k = 0; k < n; k++) {
            var i = idx[k];
            o.lat[k] = s.lat[i]; o.lon[k] = s.lon[i];
            if (o.ele) o.ele[k] = s.ele[i];
            if (o.time) o.time[k] = s.time[i];
            for (var cc in s.ch) o.ch[cc][k] = s.ch[cc][i];
        }
        return o;
    }
    function joinSegs(segs) {
        var n = segs.reduce(function (a, s) { return a + s.n; }, 0);
        var anyEle = segs.some(function (s) { return s.ele; }), anyTime = segs.some(function (s) { return s.time; });
        var o = { n: n, lat: new Float64Array(n), lon: new Float64Array(n), ele: anyEle ? new Float32Array(n).fill(NaN) : null, time: anyTime ? new Float64Array(n).fill(NaN) : null, ch: {} };
        var chs = {};
        segs.forEach(function (s) { for (var c in s.ch) if (c !== 'dist') chs[c] = 1; });
        for (var c in chs) o.ch[c] = new Float32Array(n).fill(NaN);
        var p = 0;
        segs.forEach(function (s) {
            o.lat.set(s.lat, p); o.lon.set(s.lon, p);
            if (s.ele) o.ele.set(s.ele, p);
            if (s.time) o.time.set(s.time, p);
            for (var cc in s.ch) if (o.ch[cc]) o.ch[cc].set(s.ch[cc], p);
            p += s.n;
        });
        return o;
    }

    root.GpsEdit = E;
})();
